import Images from '../../state/stores/Image/images';
import { BasicModalWithButton } from '../Modal/BasicModalWithButton';
import { EVENTS_NAMES } from '../../utils/analytics/eventsNames';
import { FEEDBACK_WHEN_CHANGING_PROGRAM } from '../../state/mutations/User';
import {Image, Platform, View} from 'react-native';
import { PresentFeedbackProgram } from '../Answer/AnswerSelect/Presentation/PresentFeedbackProgram';
import { answerFeedbackWhenChangingProgram } from '../../config/other/feedbackAfterChangingActivePath';
import { captureException } from '../../utils/sentry/sentry';
import { logAmplitude } from '../../utils/analytics/analytics';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useStores } from '../../hooks/useStores';

type ModalFeedbackWhenChangingProgramProps = {
  /** told whether one of the two modals is on screen, so the dashboard tour can wait */
  onOpenChange?: (open: boolean) => void;
}

const _ModalFeedbackWhenChangingProgram = ({onOpenChange}: ModalFeedbackWhenChangingProgramProps) => {
  const rootStore = useStores();
  const modalFeedbackForChangingPath = !!rootStore?.user?.needCollectFeedbackAfterProgramChanged;
  const labels = rootStore?.language.translate.MOBILE?.HOME?.MODAL_FEEDBACK;
  const [valueAnswer, setValueAnswer] = useState<string | undefined>(undefined);

  const [secondModalVisibility, setSecondModalVisibility] = useState<boolean>(false);
  // iOS cannot present a modal while another one is still animating out: opened
  // in the same tick as the first one closed, the thank-you modal never showed
  // (while its state said it did). It opens once the first one is hidden.
  const [thanksPending, setThanksPending] = useState<boolean>(false);
  const open = modalFeedbackForChangingPath || thanksPending || secondModalVisibility;
  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  const previousPath = rootStore?.path?.getPath(rootStore?.user?.previousProgram || '');
  const newPath = rootStore?.path?.getPath(rootStore?.user?.activePath || '');

  const category = rootStore?.path.getCategory(previousPath?._id as string);


  const [feedback] = useMutation(FEEDBACK_WHEN_CHANGING_PROGRAM, {fetchPolicy:'no-cache'});
  const disabled = !valueAnswer;

  const onDismissModelFeedbackAfterChangedPath = () => {
    rootStore?.user?.setNeedCollectFeedbackAfterProgramChanged(false);
  };

  const onDismissSecondModal = () => {
    setSecondModalVisibility(false);
    setValueAnswer(undefined);
  };


  return(
    <>
      <BasicModalWithButton
        onDismiss={onDismissModelFeedbackAfterChangedPath}
        isVisible={modalFeedbackForChangingPath}
        onModalHide={() => {
          if (thanksPending) {
            setThanksPending(false);
            setSecondModalVisibility(true);
          }
        }}
        buttonText={labels?.SHARE}
        title={labels?.TITLE}
        onPressButton={() => {
          const valVariables =
            {
              type: valueAnswer,
              platform: Platform.OS,
              previousProgram: rootStore?.user?.previousProgram,
              newProgram: rootStore?.user?.activePath
            };
          try {
            feedback({
              variables:
              {feedback : valVariables
              }
            });
            logAmplitude(EVENTS_NAMES.PROGRAM_MODIFIED, {
              feedbackType: valueAnswer,
              previousPathId: previousPath?.humanReadableId,
              newPathId: newPath?.humanReadableId,
              moduleId: category?.humanReadableId,
            });
          } catch(error) {
            captureException(error);
          }
          setThanksPending(true);
          onDismissModelFeedbackAfterChangedPath();

        }}
        buttonDisabled={disabled }
      >
        {Object.keys(answerFeedbackWhenChangingProgram).map(key => {
          return(
            <PresentFeedbackProgram
              key={key}
              label={labels?.[key]}
              selected={valueAnswer === key}
              onPress={() => {setValueAnswer(key);}}
            />
          );
        })}

      </BasicModalWithButton>
      <BasicModalWithButton
        onDismiss={onDismissSecondModal}
        isVisible={secondModalVisibility}
        buttonText={labels?.START_MY_NEW_PROGRAM}
        title={labels?.THANK_YOU}
        onPressButton={onDismissSecondModal}
        buttonDisabled={false }
      >
        <View style={{alignItems: 'center', width: '100%'}}>
          <Image source={Images.illustrations.feedback_heart} style={{width:104, height: 104}} />
        </View>
      </BasicModalWithButton>
    </>
  );
};

export const ModalFeedbackWhenChangingProgram= observer(_ModalFeedbackWhenChangingProgram);